import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'gatsby';
import { Button, Dropdown } from 'semantic-ui-react';
import { IState } from '@pvolve/sdk/src/redux/selectors';
import { WindowLocation } from '@reach/router';

import Actions from 'src/state/root-actions';
import withLocation from 'src/components/withLocation';
import Selectors from 'src/state/root-selectors';
import { isAccountPage } from 'src/utils/route-utils';
import { Icon } from 'src/components/shared';
import { CartItemCount } from 'src/components/shopify';

import * as Styles from 'src/styles/header.module.scss';
import { isEmpty } from 'lodash';
import LinkWithPrevPath from 'src/components/LinkWithPrevPath';

const mapStateToProps = (state: IState) => ({
    loggedIn: Selectors.auth.loggedIn(state),
    user: Selectors.auth.user(state),
    entitled: Selectors.auth.entitled(state),
    hasSubscriptions: Selectors.subscriptions.hasSubscriptionHistory(state),
    shopifyUrl: Selectors.config.shopifyUrl(state),
    headerCTA: Selectors.config.headerCTA(state),
});

const connector = connect(mapStateToProps, {
    logout: () => Actions.auth.logout.trigger(),
});

interface HeaderMenuProps extends ConnectedProps<typeof connector> {
    location: WindowLocation;
}

const HeaderMenu = ({
    loggedIn,
    logout,
    user,
    entitled,
    hasSubscriptions,
    location: { pathname },
    shopifyUrl,
    headerCTA,
}: HeaderMenuProps) => {
    const [isMenuOpen, setMenuOpen] = useState(false);

    const openMenu = () => setMenuOpen(true);
    const closeMenu = () => setMenuOpen(false);

    const title = user?.given_name ? user.given_name : 'My Account';
    const showSubscriptions = entitled || hasSubscriptions;

    let activeMenuItem;

    if (isAccountPage(pathname)) {
        if (pathname.match(/subscription/)) {
            activeMenuItem = 'subscription';
        } else if (pathname.match(/orders/)) {
            activeMenuItem = 'orders';
        } else if (pathname.match(/communications/)) {
            activeMenuItem = 'communications';
        } else if (pathname.match(/^\/settings\/?$/)) {
            activeMenuItem = 'settings';
        } else if (pathname.match(/^\/account\/?$/)) {
            activeMenuItem = 'account';
        }
    }

    if (!loggedIn) {
        return (
            <div>
                <LinkWithPrevPath className={Styles.headerLogin} to="/continue">
                    Login
                </LinkWithPrevPath>
                {isEmpty(headerCTA) ? (
                    <Button
                        as="a"
                        size="small"
                        href={`/checkout/?sku=1month_trial14&v=v1`}
                        content="Try for Free"
                    />
                ) : (
                    <Button as={Link} size="small" to={headerCTA.link} content={headerCTA.copy} />
                )}
            </div>
        );
    }

    return (
        <div className={Styles.rightItems}>
            <a href={`${shopifyUrl}/cart`}>
                <Icon name="pv-shopping-bag" size={21} />
                <CartItemCount shopifyUrl={shopifyUrl} />
            </a>
            &nbsp;&nbsp;&nbsp;
            <Dropdown
                className={Styles.headerDropdownTitle}
                onBlur={closeMenu}
                onFocus={openMenu}
                icon={isMenuOpen ? 'angle up' : 'angle down'}
                direction="left"
                floating
                trigger={
                    <span tabIndex="1" title={title}>
                        {' '}
                        {title}{' '}
                    </span>
                }
            >
                <Dropdown.Menu className={Styles.headerDropdownMenu} size="large" tabIndex="2">
                    <Dropdown.Item
                        className={Styles.headerDropdownItem}
                        as={Link}
                        active={activeMenuItem === 'account'}
                        text="Account"
                        to="/account"
                    />
                    {showSubscriptions && (
                        <Dropdown.Item
                            className={Styles.headerDropdownItem}
                            as={Link}
                            active={activeMenuItem === 'settings'}
                            text="Settings"
                            to="/account/settings"
                        />
                    )}
                    {showSubscriptions && (
                        <Dropdown.Item
                            className={Styles.headerDropdownItem}
                            as={Link}
                            active={activeMenuItem === 'subscription'}
                            text="Subscription"
                            to="/account/subscription"
                        />
                    )}
                    <Dropdown.Item
                        className={Styles.headerDropdownItem}
                        as={Link}
                        active={activeMenuItem === 'communications'}
                        text="Communications"
                        to="/account/communications"
                    />
                    <Dropdown.Item
                        className={Styles.headerDropdownItem}
                        text="Logout"
                        onClick={logout}
                    />
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default connector(withLocation(HeaderMenu));
